import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, Loader2, PanelRight, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useChat, useNombreUsuario } from "@/contexts/ChatContext";
import {
  getChat,
  getChatMensajes,
  sendChatMensaje,
  editChatMensaje,
  deleteChatMensaje,
  markChatLeido,
  getTrabajosCliente,
  updateChatMiembros,
  addClienteEnlace,
  getApiErrorMessage,
} from "@/lib/api";
import { nombreVisible, nombreLegal } from "@/lib/clienteNombre";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { MessageList } from "@/components/chat/MessageList";
import { Composer } from "@/components/chat/Composer";
import { TrabajosBar } from "@/components/chat/TrabajosBar";
import { NuevoTrabajoDialog, TrabajoDetalleDialog } from "@/components/chat/TrabajoDialogs";
import { ClienteSheet } from "@/components/chat/ClienteSheet";
import { MiembrosDialog, AvataresApilados } from "@/components/chat/MiembrosDialog";

const CERCA_DEL_FINAL_PX = 160;

function unirMensajes(actuales, nuevos) {
  const porId = new Map(actuales.map((m) => [m.id, m]));
  nuevos.forEach((m) => porId.set(m.id, m));
  return [...porId.values()].sort((a, b) => (a.fecha_creacion < b.fecha_creacion ? -1 : 1));
}

export default function ChatConversacion() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribe, marcarLeidoLocal, setChatAbierto, refreshChats } = useChat();
  const nombreDe = useNombreUsuario();
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const leerTimerRef = useRef(null);
  const ajusteScrollRef = useRef(null);
  const [cliente, setCliente] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargandoAntiguos, setCargandoAntiguos] = useState(false);
  const [trabajos, setTrabajos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [primerNoLeidoId, setPrimerNoLeidoId] = useState(null);
  const [escribiendo, setEscribiendo] = useState({});
  const [hayNuevosAbajo, setHayNuevosAbajo] = useState(false);
  const [fichaOpen, setFichaOpen] = useState(false);
  const [miembrosOpen, setMiembrosOpen] = useState(false);
  const [nuevoTrabajo, setNuevoTrabajo] = useState(null);
  const [abrirTrabajoId, setAbrirTrabajoId] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);

  const yo = user?.email;
  const miembros = cliente?.miembros || [];

  const cercaDelFinal = () => {
    const el = scrollRef.current;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < CERCA_DEL_FINAL_PX;
  };

  const irAlFinal = useCallback((suave = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: suave ? "smooth" : "auto" });
    setHayNuevosAbajo(false);
  }, []);

  const marcarLeido = useCallback(() => {
    clearTimeout(leerTimerRef.current);
    leerTimerRef.current = setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      marcarLeidoLocal(clienteId);
      markChatLeido(clienteId).catch(() => {});
    }, 600);
  }, [clienteId, marcarLeidoLocal]);

  const cargarCliente = useCallback(async () => {
    try {
      const data = await getChat(clienteId);
      setCliente(data.cliente);
      return data.cliente;
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        toast.error(getApiErrorMessage(err, "No tienes acceso a este chat"));
        refreshChats();
        navigate("/chat", { replace: true });
      }
      return null;
    }
  }, [clienteId, navigate, refreshChats]);

  // Carga inicial
  useEffect(() => {
    let cancelado = false;
    setChatAbierto(clienteId);
    (async () => {
      setLoading(true);
      const c = await cargarCliente();
      if (!c || cancelado) return;
      try {
        const [m, t] = await Promise.all([getChatMensajes(clienteId), getTrabajosCliente(clienteId)]);
        if (cancelado) return;
        const lista = m.mensajes || [];
        setMensajes(lista);
        setHayMas(!!m.hay_mas);
        setTrabajos(t.trabajos || []);
        const primero = m.ultimo_leido ? lista.find((x) => x.fecha_creacion > m.ultimo_leido && x.autor !== yo) : null;
        setPrimerNoLeidoId(primero?.id || null);
        ajusteScrollRef.current = { tipo: "inicial", ancla: primero?.id };
      } catch (err) {
        toast.error(getApiErrorMessage(err, "No se pudo cargar el chat"));
      } finally {
        if (!cancelado) setLoading(false);
      }
      marcarLeido();
    })();
    return () => {
      cancelado = true;
      setChatAbierto(null);
      clearTimeout(leerTimerRef.current);
    };
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Posición del scroll tras pintar: al primer no leído / al final / mantener al cargar antiguos
  useLayoutEffect(() => {
    const ajuste = ajusteScrollRef.current;
    const el = scrollRef.current;
    if (!ajuste || !el) return;
    ajusteScrollRef.current = null;
    if (ajuste.tipo === "inicial") {
      const ancla = ajuste.ancla && document.getElementById("chat-primer-no-leido");
      if (ancla) el.scrollTop = ancla.offsetTop - 80;
      else el.scrollTop = el.scrollHeight;
    } else if (ajuste.tipo === "antiguos") {
      el.scrollTop = el.scrollHeight - ajuste.altura + ajuste.top;
    } else if (ajuste.tipo === "final") {
      el.scrollTop = el.scrollHeight;
    }
  }, [mensajes, loading]);

  // Tiempo real
  useEffect(() => {
    const timers = {};
    const off = subscribe((ev) => {
      if (ev.type === "reconectado") {
        getChatMensajes(clienteId).then((m) => setMensajes((prev) => unirMensajes(prev, m.mensajes || []))).catch(() => {});
        getTrabajosCliente(clienteId).then((t) => setTrabajos(t.trabajos || [])).catch(() => {});
        return;
      }
      if (ev.cliente_id !== clienteId) return;
      switch (ev.type) {
        case "mensaje_nuevo": {
          const m = ev.mensaje;
          const pegado = cercaDelFinal();
          setMensajes((prev) => unirMensajes(prev, [m]));
          setEscribiendo((prev) => {
            if (!prev[m.autor]) return prev;
            const next = { ...prev };
            delete next[m.autor];
            return next;
          });
          if (m.autor === yo || pegado) ajusteScrollRef.current = { tipo: "final" };
          else setHayNuevosAbajo(true);
          if (m.autor !== yo) marcarLeido();
          break;
        }
        case "mensaje_editado":
          setMensajes((prev) => prev.map((x) => (x.id === ev.mensaje.id ? ev.mensaje : x)));
          break;
        case "trabajo_actualizado":
          setTrabajos((prev) => (prev.some((t) => t.id === ev.trabajo.id) ? prev.map((t) => (t.id === ev.trabajo.id ? ev.trabajo : t)) : [...prev, ev.trabajo]));
          break;
        case "trabajo_eliminado":
          setTrabajos((prev) => prev.filter((t) => t.id !== ev.trabajo_id));
          break;
        case "cliente_actualizado":
        case "miembros_actualizados":
          cargarCliente();
          break;
        case "escribiendo":
          setEscribiendo((prev) => ({ ...prev, [ev.email]: true }));
          clearTimeout(timers[ev.email]);
          timers[ev.email] = setTimeout(() => {
            setEscribiendo((prev) => {
              const next = { ...prev };
              delete next[ev.email];
              return next;
            });
          }, 3500);
          break;
        default:
      }
    });
    return () => {
      off();
      Object.values(timers).forEach(clearTimeout);
    };
  }, [clienteId, subscribe, yo, marcarLeido, cargarCliente]);

  // Volver a la pestaña: marcar como leído
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && marcarLeido();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [marcarLeido]);

  const cargarAntiguos = async () => {
    if (!hayMas || cargandoAntiguos || mensajes.length === 0) return;
    setCargandoAntiguos(true);
    const el = scrollRef.current;
    try {
      const m = await getChatMensajes(clienteId, { antes: mensajes[0].fecha_creacion });
      ajusteScrollRef.current = { tipo: "antiguos", altura: el.scrollHeight, top: el.scrollTop };
      setMensajes((prev) => unirMensajes(prev, m.mensajes || []));
      setHayMas(!!m.hay_mas);
    } catch (_) {
      toast.error("No se pudieron cargar mensajes anteriores");
    } finally {
      setCargandoAntiguos(false);
    }
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 120) cargarAntiguos();
    if (cercaDelFinal()) setHayNuevosAbajo(false);
  };

  const enviar = async (payload) => {
    const m = await sendChatMensaje(clienteId, payload);
    setMensajes((prev) => unirMensajes(prev, [m]));
    setPrimerNoLeidoId(null);
    ajusteScrollRef.current = { tipo: "final" };
  };

  const compartirFiscales = async () => {
    try {
      await enviar({ tipo: "tarjeta_cliente", texto: "" });
      setFichaOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo enviar"));
    }
  };

  const guardarMiembros = async (lista) => {
    try {
      const c = await updateChatMiembros(clienteId, lista);
      setCliente(c);
      if (!lista.includes(yo) && !user?.permissions?.admin) {
        refreshChats();
        navigate("/chat", { replace: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudieron guardar los miembros"));
      throw err;
    }
  };

  const fijarEnlace = async (url) => {
    try {
      const enlace = await addClienteEnlace(clienteId, { url });
      setCliente((c) => (c ? { ...c, enlaces: [...(c.enlaces || []), enlace] } : c));
      toast.success("Enlace guardado en la ficha");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo guardar el enlace"));
    }
  };

  const upsertTrabajo = (t) => setTrabajos((prev) => (prev.some((x) => x.id === t.id) ? prev.map((x) => (x.id === t.id ? t : x)) : [...prev, t]));

  const quienesEscriben = Object.keys(escribiendo).filter((e) => e !== yo).map(nombreDe);

  if (loading && !cliente) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!cliente) return null;

  return (
    <div
      className="chat-conversacion h-full flex flex-col relative"
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) {
          e.preventDefault();
          setArrastrando(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setArrastrando(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        setArrastrando(false);
        composerRef.current?.subirArchivos(e.dataTransfer.files);
      }}
    >
      <header className="shrink-0 flex items-center gap-2 md:gap-3 px-2 md:px-4 h-16 border-b bg-background">
        <button type="button" onClick={() => navigate("/chat")} className="md:hidden h-10 w-8 shrink-0 flex items-center justify-center -mr-1" aria-label="Volver a los chats">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => setFichaOpen(true)} className="flex items-center gap-3 min-w-0 flex-1 text-left rounded-lg -ml-1 pl-1 py-1 hover:bg-muted/50">
          <ClienteLogo cliente={cliente} size={40} />
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="font-semibold text-sm truncate">{nombreVisible(cliente)}</span>
              {cliente.estado === "inactivo" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">Inactivo</span>}
            </span>
            <span className="block text-xs text-muted-foreground truncate">
              {quienesEscriben.length > 0 ? (
                <span className="text-primary">{quienesEscriben.join(", ")} {quienesEscriben.length > 1 ? "están" : "está"} escribiendo…</span>
              ) : (
                nombreLegal(cliente) || `${miembros.length} miembro${miembros.length === 1 ? "" : "s"}`
              )}
            </span>
          </span>
        </button>
        <button type="button" onClick={() => setMiembrosOpen(true)} className="flex rounded-full hover:opacity-80 shrink-0" title="Miembros">
          <AvataresApilados emails={miembros} max={4} size={28} />
        </button>
        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => setFichaOpen(true)} title="Ficha del cliente">
          <PanelRight className="w-5 h-5" />
        </Button>
      </header>

      <TrabajosBar
        trabajos={trabajos}
        onNuevo={() => setNuevoTrabajo({ titulo: "" })}
        onAbrir={setAbrirTrabajoId}
      />

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {cargandoAntiguos && (
          <div className="flex justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!hayMas && mensajes.length > 0 && <p className="text-center text-[11px] text-muted-foreground pt-6">Inicio de la conversación</p>}
        <MessageList
          mensajes={mensajes}
          yo={yo}
          cliente={cliente}
          primerNoLeidoId={primerNoLeidoId}
          onAbrirTrabajo={(id) => (trabajos.some((t) => t.id === id) ? setAbrirTrabajoId(id) : toast.message("Ese trabajo ya no existe"))}
          onCrearTrabajo={(m) => setNuevoTrabajo({ titulo: (m.texto || "").slice(0, 300), mensaje_origen_id: m.id })}
          onFijarEnlace={fijarEnlace}
          onEditar={async (m, texto) => {
            try {
              const e = await editChatMensaje(clienteId, m.id, texto);
              setMensajes((prev) => prev.map((x) => (x.id === e.id ? e : x)));
            } catch (err) {
              toast.error(getApiErrorMessage(err, "No se pudo editar"));
            }
          }}
          onBorrar={async (m) => {
            try {
              await deleteChatMensaje(clienteId, m.id);
              setMensajes((prev) => prev.map((x) => (x.id === m.id ? { ...x, eliminado: true, texto: "", adjuntos: [] } : x)));
            } catch (err) {
              toast.error(getApiErrorMessage(err, "No se pudo eliminar"));
            }
          }}
        />
      </div>

      {hayNuevosAbajo && (
        <button
          type="button"
          onClick={() => irAlFinal(true)}
          className="absolute right-4 bottom-24 z-10 flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground shadow-lg px-3 h-9 text-xs font-medium"
        >
          <ArrowDown className="w-3.5 h-3.5" /> Nuevos mensajes
        </button>
      )}

      <Composer
        ref={composerRef}
        clienteId={clienteId}
        miembros={miembros}
        yo={yo}
        onEnviar={enviar}
        onCrearTrabajo={(titulo) => setNuevoTrabajo({ titulo })}
      />

      {arrastrando && (
        <div className="absolute inset-0 z-30 m-2 rounded-2xl border-2 border-dashed border-primary bg-primary/5 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2 pointer-events-none">
          <UploadCloud className="w-8 h-8 text-primary" />
          <p className="text-sm font-medium text-primary">Suelta para adjuntar</p>
        </div>
      )}

      <NuevoTrabajoDialog
        open={!!nuevoTrabajo}
        onOpenChange={(o) => !o && setNuevoTrabajo(null)}
        clienteId={clienteId}
        miembros={miembros}
        inicial={nuevoTrabajo}
        onSaved={upsertTrabajo}
      />
      <TrabajoDetalleDialog
        trabajo={trabajos.find((t) => t.id === abrirTrabajoId) || null}
        onOpenChange={(o) => !o && setAbrirTrabajoId(null)}
        miembros={miembros}
        onSaved={upsertTrabajo}
        onDeleted={(id) => setTrabajos((prev) => prev.filter((t) => t.id !== id))}
      />
      <ClienteSheet open={fichaOpen} onOpenChange={setFichaOpen} cliente={cliente} onChange={setCliente} onCompartirFiscales={compartirFiscales} />
      <MiembrosDialog
        open={miembrosOpen}
        onOpenChange={setMiembrosOpen}
        titulo="Miembros del chat"
        descripcion="Solo estas personas ven la conversación y pueden recibir trabajos."
        inicial={miembros}
        onConfirm={guardarMiembros}
      />
    </div>
  );
}
